// Builds the developer guide (Markdown) for apps that make and use data packages, using this site's real address.
const guide = (root, apps) => {
  const app = apps[0] ?? { id: "mission-ready", name: "Mission Ready", units: [] };
  const unit = app.units?.[0]?.id ?? "<team-id>";
  const base = `${root}/${app.id}/${unit}`;
  const appList = apps.length
    ? apps.map((a) => `- **${a.name}** (\`${a.id}\`): ${a.units?.length ? a.units.map((u) => `${u.name} (\`${u.id}\`)`).join(", ") : "no teams yet"}`).join("\n")
    : "- No apps set up yet.";
  return `# Data packages API: developer guide

Aero Soft Solutions Pty Ltd · generated ${new Date().toISOString().slice(0, 10)} from ${window.location.origin}/apps/bundles

This is how an app that **makes and uses data packages** (Seed, Config, Deployment and Return \`.aeropack\` files) talks to the Aero Soft Solutions package service.

## 1. Concepts

| Term | Meaning |
|---|---|
| App | A product that uses packages, e.g. ${app.name} (\`${app.id}\`). |
| Team | A unit or deployment group within an app. Each team has its own package list and its own token. On the admin page it is called a *unit*. |
| Package type | \`seed\`, \`config\`, \`deployment\` or \`return\` (the \`package_type\` in the package's \`info.json\`). |
| Current package | For each team and type, the newest package that is saved and not hidden. Admins roll a team back by hiding a package. |
| Activation code | A one-time code an admin gives the team, e.g. \`K7QX-M2PA-9RTD-LDKD\`. It works once and expires after 14 days. |
| Team token | The secret the app gets by redeeming the activation code. It is the only credential the app uses after that. |

Apps and teams set up right now:

${appList}

## 2. Getting a team token: activate once

1. An admin adds the team on the Data packages page (or uses **New activation code** on an existing team) and gives the activation code to the team.
2. The team enters the code in your app **once**. Your app swaps it for a token:

\`\`\`http
POST ${root}/activate
Content-Type: application/json

{ "code": "K7QX-M2PA-9RTD-LDKD", "device_name": "BS26 laptop 1" }
\`\`\`

Response \`200\`:

\`\`\`json
{
  "token": "dpt_...",
  "app_id": "${app.id}", "app": "${app.name}",
  "unit_id": "${unit}", "unit": "Team name", "group_id": "dg_...",
  "can_publish": false,
  "api_base": "${base}"
}
\`\`\`

- **Store \`token\` and \`api_base\` securely** (OS keychain or encrypted settings). The token is never shown again, and the server only keeps a hash of it.
- The code is spent the moment it is redeemed. Wrong, used or expired codes return \`401\`.
- The token is **not active until a code is redeemed**. When an admin issues a new code for a team that already has a token, the old token keeps working **until the new code is redeemed**, then it stops (\`401\`).
- Admins can **revoke** a team at any time. Every call then returns \`401\`, and the team needs a new code.
- Codes are case-insensitive, and spaces and dashes are ignored.

## 3. Calling the API

Send the token on every request:

\`\`\`
Authorization: Bearer dpt_...
\`\`\`

(\`X-API-Key: dpt_...\` is also accepted.) All team endpoints live under \`api_base\` = \`${root}/{app_id}/{unit_id}\`. A token only works for its own team's URL.

| Method & path | What it does |
|---|---|
| \`GET  {api_base}/me\` | Who am I: app, team, \`group_id\`, \`can_publish\`. Publishers also get the list of all teams. |
| \`GET  {api_base}/current\` | The current package of each type: \`{ "seed": {...}|null, "config": ..., "deployment": ..., "return": ... }\` |
| \`GET  {api_base}/latest?type=config\` | The current package of one type. Without \`type\` it gives the newest non-Return package. \`404\` if there is none. |
| \`GET  {api_base}/versions?type=seed\` | Every visible version, newest first (\`type\` optional). |
| \`GET  {api_base}/download?type=config\` | Streams the current file of that type. Use \`?id=<package id>\` for an exact version. |
| \`POST {api_base}/uploads\` | Start a package upload (see section 5). |
| \`GET  {api_base}/packages/{id}\` | Status of a package you uploaded (or one sent to you). |

### Package record (returned by /current, /latest and /versions)

\`\`\`json
{
  "id": "be98...",
  "app": "${app.name}", "unit": "Team name",
  "package_type": "config",
  "version": "336 alpha",
  "min_build": 334,
  "file_date": "2026-10-28",
  "filename": "missionready-config-2026-10-28.aeropack",
  "size": 2795310,
  "sha256": "64 hex characters: SHA-256 of the whole file",
  "contents_verified": true,
  "description": "Configuration update for All units. ...",
  "package_info": { "label": "...", "destination": "All units", "created_by": "...", "app_build": "336" },
  "uploaded_at": "2026-10-28T06:39:00+00:00",
  "download_url": "${base}/download?id=be98..."
}
\`\`\`

## 4. Recommended sync loop (consuming packages)

1. \`GET {api_base}/current\` on start-up and then every 15 minutes or so (or when the user taps *Check for updates*).
2. For each type you care about, compare \`id\` (or \`sha256\`) with what you last applied. If it's the same, do nothing.
3. If \`min_build\` is higher than your app's build, don't apply it. Tell the user to update the app.
4. Download \`download_url\` with the same Authorization header. Write to a temp file, **check the SHA-256 of the whole file equals \`sha256\`** (the \`X-Package-SHA256\` response header carries it too), then check each \`collections/*.jsonl\` against \`manifest.json\` as you already do.
5. Apply the package, then remember its \`id\` and \`sha256\`.
6. On \`401\`, the token is gone (revoked or replaced): ask the user for a new activation code.

\`\`\`bash
curl -s -H "Authorization: Bearer $TOKEN" ${base}/current
curl -fL -H "Authorization: Bearer $TOKEN" -o config.aeropack "${base}/download?type=config"
sha256sum config.aeropack
\`\`\`

## 5. Publishing packages (uploading)

Files of up to 900 MB are sent in **4 MB chunks**. The service saves them straight to file storage and works out the SHA-256 as it goes.

**Who can publish where**

- Every team can publish packages of any type **to itself**. A Return goes into the team's own list for base to collect.
- A team with **"Can publish to other teams"** (switched on by an admin, shown as \`can_publish: true\`) can also:
  - send to named teams with \`"unit_ids": ["team-a", "team-b"]\` (ids from \`/me\`), or
  - for a Deployment, pass \`"destination": { "id": "dg_...", "name": "BS26 aVN R2" }\` (straight from \`info.json\`). The package goes to the team with that \`group_id\` or name, and **a new team is added if none exists** (an admin then makes its activation code).
- Anything else returns \`403\`.

### Step 1: start

\`\`\`http
POST {api_base}/uploads
{ "filename": "missionready-return-2026-10-28.aeropack", "size": 1823311 }
\`\`\`
→ \`{ "upload_id": "...", "chunk_size": 4194304, "total_chunks": 1 }\`

### Step 2: send each chunk (raw bytes, any order, retry on failure)

\`\`\`http
PUT {api_base}/uploads/{upload_id}/chunks/{index}      (index 0 … total_chunks-1)
Content-Type: application/octet-stream
<bytes index*chunk_size … +chunk_size; the last chunk is the remainder>
\`\`\`

Each chunk must be exactly \`chunk_size\` bytes except the last one (\`400\` otherwise, so send it again). Upload sessions last 24 hours. \`DELETE {api_base}/uploads/{upload_id}\` abandons one.

### Step 3: complete

\`\`\`http
POST {api_base}/uploads/{upload_id}/complete
{
  "package_type": "return",                 // seed | config | deployment | return (required)
  "version": "336 alpha",                   // required, shown to people
  "sha256": "<SHA-256 of the whole file>",  // required, checked after saving
  "min_build": 334,                         // optional
  "file_date": "2026-10-28",                // optional, default today
  "description": "Return sync from BS26",   // optional
  "contents_verified": true,                // optional: you checked manifest.json yourself
  "package_info": { "label": "Return sync", "created_by": "..." },  // optional, max 12 short text fields
  "unit_ids": [],                           // publishers only (see above)
  "destination": null                       // publishers only, Deployment
}
\`\`\`
→ \`{ "id": "<package id>", "status": "processing", "unit_ids": ["${unit}"], "status_url": "{api_base}/packages/<id>" }\`

### Step 4: wait until it is saved

Poll \`GET status_url\` every 2 seconds until \`status\` is no longer \`processing\`:

- \`ready\`: done. \`checksum_match\` is \`true\` when the saved file's SHA-256 equals the one you sent. If it is \`false\`, the file arrived damaged: upload it again.
- \`failed\`: see \`error\` and upload again.
- \`deleted\`: an admin removed or stopped it.

### Minimal Python example

\`\`\`python
import hashlib, os, requests, time
API, TOKEN = "${base}", os.environ["TEAM_TOKEN"]
H = {"Authorization": f"Bearer {TOKEN}"}
path = "missionready-return-2026-10-28.aeropack"
data = open(path, "rb").read()  # stream from disk for very large files
s = requests.post(f"{API}/uploads", json={"filename": os.path.basename(path), "size": len(data)}, headers=H).json()
cs = s["chunk_size"]
for i in range(s["total_chunks"]):
    for attempt in range(5):
        r = requests.put(f"{API}/uploads/{s['upload_id']}/chunks/{i}", data=data[i*cs:(i+1)*cs], headers=H)
        if r.ok: break
        time.sleep(2 * (attempt + 1))
    r.raise_for_status()
c = requests.post(f"{API}/uploads/{s['upload_id']}/complete", headers=H, json={
    "package_type": "return", "version": "336 alpha", "sha256": hashlib.sha256(data).hexdigest(), "min_build": 334}).json()
while (st := requests.get(c["status_url"], headers=H).json())["status"] == "processing":
    time.sleep(2)
assert st["status"] == "ready" and st["checksum_match"], st
\`\`\`

## 6. Errors

| Code | Meaning | What the app should do |
|---|---|---|
| 400 | Bad input (wrong chunk size, unknown team or type, missing parts) | Fix the request, or resend the chunk |
| 401 | Code wrong/used/expired, or token missing, revoked or replaced | Ask the user for a new activation code |
| 403 | Team isn't allowed to publish to other teams | Publish to itself, or ask an admin |
| 404 | No such package yet, or it was hidden or removed | Try again later |
| 409 | Upload already completed or stopped | Start a new upload |
| 422 | A field is missing or in the wrong format | Fix the request |
| 502 | File storage didn't answer | Retry with back-off |

Errors are JSON: \`{ "detail": "plain-English reason" }\`.

## 7. Security notes

- Keep the token out of logs and URLs, and only send it in the Authorization header over HTTPS.
- One token per team. Teams can't read each other's packages.
- The service stores only hashes of tokens and codes. Lost token = new activation code.
- Every download by a team's app appears in the package's download history on the admin page.
`;
};

export const downloadGuide = (apps) => {
  const md = guide(`${window.location.origin}/api/bundles`, Array.isArray(apps) ? apps : []);
  const url = URL.createObjectURL(new Blob([md], { type: "text/markdown;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = "data-packages-api-guide.md";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};
